// ============================================================
// FICHIER : boutique/modules/session.js
// RÔLE : Vérifier / créer / transmettre la session.
//        À l'ouverture du widget, on lit la session stockée
//        (localStorage) ; si elle n'existe pas, on en crée une
//        via GET /session/new et on la transmet au serveur à
//        chaque requête via le header X-Session-Token.
// ============================================================

window.Toilesia = window.Toilesia || {};
(function () {
    'use strict';

    const config = Toilesia.config;
    let _uuid = null; // cache en mémoire (évite les relectures du stockage)

    function lireStockage() {
        try {
            return window.localStorage.getItem(config.cleSession) || '';
        } catch (e) {
            return '';
        }
    }

    function ecrireStockage(uuid) {
        try {
            window.localStorage.setItem(config.cleSession, uuid);
        } catch (e) {
            // stockage indisponible (navigation privée restreinte, etc.)
        }
    }

    /**
     * Retourne l'UUID de session s'il existe déjà en stockage.
     * @returns {string}
     */
    function recuperer() {
        if (!_uuid) {
            _uuid = lireStockage();
        }
        return _uuid;
    }

    /**
     * Garantit qu'une session existe : renvoie l'UUID existant ou en
     * crée une via GET /session/new puis la stocke.
     * ⚠️ Verrou « single-flight » PARTAGÉ (Toilesia.__sessionEnCours) avec
     *    toilesia-identite.js : une seule création de session en vol, même
     *    quand les deux modules s'initialisent en parallèle sur une page
     *    produit. Évite 2 sessions + 2 utilisateurs anonymes et le rate
     *    limit de /session/new (2 créations / 10 s).
     * @returns {Promise<string>} UUID de session
     */
    async function garantir() {
        const existante = recuperer();
        if (existante) {
            return existante;
        }

        if (!Toilesia.__sessionEnCours) {
            Toilesia.__sessionEnCours = creerNouvelleSession().finally(function () {
                Toilesia.__sessionEnCours = null;
            });
        }

        const uuid = await Toilesia.__sessionEnCours;
        if (uuid) {
            _uuid = uuid;
            ecrireStockage(uuid);
        }
        return uuid;
    }

    /**
     * Crée effectivement une session (appel réseau /session/new).
     * Re-vérifie le stockage avant l'appel : un autre module a pu en
     * créer une entre-temps (évite un second appel réseau inutile).
     * @returns {Promise<string>}
     */
    async function creerNouvelleSession() {
        const existante = recuperer();
        if (existante) {
            return existante;
        }

        const reponse = await ToilesiaAuth.apiCall('session/new', 'GET');
        if (reponse.succes && reponse.session_id) {
            _uuid = reponse.session_id;
            ecrireStockage(_uuid);
            return _uuid;
        }
        // Diagnostic : on joint le message technique (ex. « Failed to fetch »)
        // et l'URL appelée — c'est ce qui permet de distinguer un certificat
        // TLS refusé, un blocage CORS ou un appareil hors ligne.
        const detail = reponse.erreur_technique ? ' : ' + reponse.erreur_technique : '';
        const url = reponse.url ? ' [' + reponse.url + ']' : '';
        throw new Error(
            'Impossible de créer la session (' + (reponse.erreur || 'réponse vide') + ')' + detail + url
        );
    }

    /**
     * Headers de session à joindre à chaque requête.
     * @returns {object} ex. { 'X-Session-Token': 'abc123...' } (vide si absente)
     */
    function headers() {
        const uuid = recuperer();
        return uuid ? { 'X-Session-Token': uuid } : {};
    }

    Toilesia.session = {
        recuperer: recuperer,
        garantir: garantir,
        headers: headers
    };
})();
